// =============================================================================
// SIREPPAR - Wizard Candidatura, Paso 4: Documentación Art. 77
// =============================================================================

import { useState } from 'react';
import toast from 'react-hot-toast';
import { candidatoService } from '../../../services/api';
import type { Candidato } from './CandidaturaWizardPage';

interface Props {
  candidato: Candidato;
  onSaved: () => void;
  onBack: () => void;
  onNext: () => void;
}

const DOCUMENTOS = [
  {
    tipo: 'ACEPTACION_CANDIDATURA',
    titulo: 'Aceptación individual de la candidatura',
    descripcion: 'Formulario firmado por el candidato y el apoderado (Art. 77 inciso d, Ley XII N° 21).',
    conModelo: true,
  },
  {
    tipo: 'ANTECEDENTES_PENALES_CANDIDATO',
    titulo: 'Certificado de antecedentes penales',
    descripcion: 'Emitido por el Registro Nacional de Reincidencia (Art. 77 CE). Su validez la revisa la Secretaría al analizar el expediente.',
    conModelo: false,
  },
  {
    tipo: 'PLATAFORMA_ELECTORAL_CANDIDATURA',
    titulo: 'Plataforma electoral',
    descripcion: 'Programa de gobierno propuesto por la candidatura (Art. 77 CE).',
    conModelo: false,
  },
  {
    tipo: 'ACTA_PROCLAMACION',
    titulo: 'Acta de proclamación',
    descripcion: 'Acta de la agrupación que proclama la candidatura (Art. 77 CE).',
    conModelo: false,
  },
];

export default function Paso4Documentacion({ candidato, onSaved, onBack, onNext }: Props) {
  const [subiendo, setSubiendo] = useState<string | null>(null);

  const subir = async (tipo: string, archivo: File) => {
    setSubiendo(tipo);
    try {
      await candidatoService.subirDocumento(candidato.id, tipo, archivo);
      toast.success('Documento subido');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo subir el archivo');
    } finally {
      setSubiendo(null);
    }
  };

  const reemplazar = async (documentoId: string, tipo: string, archivo: File) => {
    setSubiendo(tipo);
    try {
      await candidatoService.reemplazarDocumento(documentoId, archivo);
      toast.success('Documento reemplazado');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo reemplazar el archivo');
    } finally {
      setSubiendo(null);
    }
  };

  const eliminar = async (documentoId: string) => {
    try {
      await candidatoService.eliminarDocumento(documentoId);
      toast.success('Documento eliminado');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo eliminar el documento');
    }
  };

  const descargarModelo = async () => {
    try {
      const res = await candidatoService.descargarModeloAceptacion();
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'Aceptacion de Candidatura.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('No se pudo descargar el modelo');
    }
  };

  const docCargado = (tipo: string) => candidato.documentos.find(d => d.tipo === tipo);
  const puedeContinuar = DOCUMENTOS.every(d => docCargado(d.tipo));

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
        Documentación exigida por el Art. 77 del Código Electoral Provincial (Ley XII N° 21) para la presentación de candidaturas.
      </div>

      <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
        {DOCUMENTOS.map(doc => {
          const cargado = docCargado(doc.tipo);
          const enCurso = subiendo === doc.tipo;
          return (
            <div key={doc.tipo} className="p-5 flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium ${
                cargado ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
              }`}>
                {cargado ? '✓' : '○'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900">{doc.titulo}</div>
                <p className="text-xs text-gray-500 mt-0.5">{doc.descripcion}</p>
                {doc.conModelo && (
                  <button type="button" onClick={descargarModelo} className="text-xs text-blue-600 hover:text-blue-800 underline mt-1.5 inline-block">
                    Descargar modelo oficial SEP
                  </button>
                )}
                {cargado && (
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="text-green-700">📄 {cargado.nombreOriginal}</span>
                    <a href={cargado.urlDescarga} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 underline">Ver</a>
                    <button type="button" onClick={() => eliminar(cargado.id)} className="text-red-500 hover:text-red-700">✕ Eliminar</button>
                  </div>
                )}
              </div>
              <label className={`flex-shrink-0 px-3 py-1.5 border rounded-lg text-xs font-medium cursor-pointer ${
                enCurso ? 'border-gray-200 text-gray-400' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}>
                {enCurso ? 'Subiendo...' : cargado ? 'Reemplazar' : 'Cargar'}
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="hidden"
                  disabled={enCurso}
                  onChange={e => {
                    const archivo = e.target.files?.[0];
                    if (!archivo) return;
                    if (cargado) reemplazar(cargado.id, doc.tipo, archivo);
                    else subir(doc.tipo, archivo);
                  }}
                />
              </label>
            </div>
          );
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-2 text-sm">Documentación obtenida de oficio por la SEP</h3>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>Certificado del Registro de Antecedentes Penales del Chubut (RAP)</li>
          <li>Consulta al Registro de Alimentantes Morosos (RAM)</li>
          <li>Verificación del Registro de Infractores</li>
        </ul>
        <p className="text-xs text-gray-500 mt-2">No es necesario que la agrupación adjunte estos certificados — la SEP los obtiene automáticamente (Art. 78 CE).</p>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">← Volver</button>
        <button
          onClick={onNext}
          disabled={!puedeContinuar}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
        >
          Continuar a declaración jurada →
        </button>
      </div>
    </div>
  );
}
